import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';

import { VirtualAssistantIcon } from '@icons/VirtualAssistantIcon';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { IUserAvatar } from '@interfaces/avatar';
import { EventRole } from '@interfaces/event';
import { APP_LOGO } from '@components/AppLogo/interfaces';
import { GenderType } from '@features/avaturn/interfaces';
import {
  IXRHrBrandTypes,
  IXRHrPersonaTypes,
  WebsocketAction,
} from '@clients/ek/interfaces';
import {
  isBroadcastEnabled as isBroadcastEnabledEk,
  isPrivateChatEnabled as isPrivateChatEnabledEk,
} from '@clients/ek/helpers';
import { getUseAvaturnHelper } from '@features/avatar/helpers';

import {
  CHAT_DRAWER_WIDTH,
  MINUTES_ALLOW_TO_JOIN,
  MINUTES_ALLOW_TO_JOIN_HOST,
} from '@constants';
import EventsService from '@services/EventsService';
import { env } from 'env';
import ROUTES from '@constants/routes';

const {
  REACT_APP_CLIENT_ENV,
  REACT_APP_MULTI_PLAYER_TAG,
  REACT_APP_TMS_CLASS_ROOMS_TAG,
  REACT_APP_MAYA_TAGS,
} = env;

const reactMultiPlayerTag = REACT_APP_MULTI_PLAYER_TAG || 'MultiPlayer';
const reactTMSClassRoomsTag = REACT_APP_TMS_CLASS_ROOMS_TAG || 'ClassRooms';

export const useClientSpecificProfileInformation = () => {
  const { user } = useMytaverse();

  const profileUserName = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        if (user.hrData) {
          const { name, nickName } = user.hrData;
          return (
            <>
              <Typography>{name}</Typography>
              {nickName ? <Typography>({nickName})</Typography> : null}
            </>
          );
        }

        return <Typography>{user.name}</Typography>;
      default:
        return <Typography>{user.name}</Typography>;
    }
  }, [user]);

  const profileEmail = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK': {
        return user.hrData ? user.hrData?.personalEmail : user.email;
      }
      default:
        return user.email;
    }
  }, [user]);

  const profileGender = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK': {
        return user.hrData
          ? user.hrData?.gender.toLowerCase() === 'm'
            ? GenderType.MALE
            : GenderType.FEMALE
          : '';
      }
      default:
        return '';
    }
  }, [user]);

  return { profileUserName, profileEmail, profileGender };
};

export const useClientSpecificVirtualAssistant = () => {
  const { t: translate } = useTranslation();

  const { currentEvent } = useMytaverseEvent();

  return useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        if (currentEvent?.eventTags && Array.isArray(currentEvent.eventTags)) {
          const MAYA_TAGS =
            REACT_APP_MAYA_TAGS?.split(',').map((x) =>
              x.toLowerCase().trim(),
            ) || [];

          const isMaya = currentEvent.eventTags?.some((tag) =>
            MAYA_TAGS.includes(tag.toLowerCase()),
          );

          if (isMaya) {
            return {
              icon: <img src={require('@public/images/assistant/maya.png')} />,
              label: translate('tooltips.maya'),
            };
          }
        }

        return {
          icon: <img src={require('@public/images/assistant/mira.png')} />,
          label: translate('tooltips.mira'),
        };

      default:
        return {
          icon: <VirtualAssistantIcon />,
          label: translate('tooltips.virtualAssistant'),
        };
    }
  }, [currentEvent?.eventTags]);
};

export const useClientSpecificHasMultiplayerTag = () => {
  const { currentEvent } = useMytaverseEvent();

  const hasMultiplayerTag = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return (
          currentEvent?.eventTags &&
          Array.isArray(currentEvent.eventTags) &&
          currentEvent.eventTags.includes(reactMultiPlayerTag)
        );
      default:
        return false;
    }
  }, [currentEvent?.eventTags]);

  return { hasMultiplayerTag };
};

export const useClientSpecificPrivateChatVisibility = () => {
  const { currentEvent } = useMytaverseEvent();
  const { user } = useMytaverse();
  const { hasMultiplayerTag } = useClientSpecificHasMultiplayerTag();

  const isPrivateChatEnabled = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return (
          isPrivateChatEnabledEk({ user, currentEvent }) && hasMultiplayerTag
        );
      default:
        return true;
    }
  }, [user, currentEvent, isPrivateChatEnabledEk]);

  return { isPrivateChatEnabled };
};

export const useClientSpecificBroadcastChatVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isBroadcastEnabled = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return currentEvent?.eventTags?.includes(reactTMSClassRoomsTag);
      default:
        return true;
    }
  }, [currentEvent]);

  return { isBroadcastEnabled };
};

export const useClientSpecificBroadcastChatInputVisibility = () => {
  const { currentEventParticipant } = useMytaverseEvent();

  const canBroadcast = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return isBroadcastEnabledEk(currentEventParticipant);
      default:
        return true;
    }
  }, [currentEventParticipant, isBroadcastEnabledEk]);

  return { canBroadcast };
};

export const useClientSpecificChatDrawerWidth = () => {
  const { isPrivateChatEnabled } = useClientSpecificPrivateChatVisibility();
  const { isBroadcastEnabled } = useClientSpecificBroadcastChatVisibility();

  if (isPrivateChatEnabled && isBroadcastEnabled) {
    return { chatDrawerWidth: CHAT_DRAWER_WIDTH.THREE_TABS };
  }

  return { chatDrawerWidth: CHAT_DRAWER_WIDTH.TWO_TABS };
};

export const useClientSpecificJoinEventSchedulerMinToJoin = () => {
  const { user } = useMytaverse();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return user?.hrData?.persona?.toUpperCase() ===
        IXRHrPersonaTypes.HR_Facilitator
        ? MINUTES_ALLOW_TO_JOIN_HOST
        : MINUTES_ALLOW_TO_JOIN;
    default:
      return MINUTES_ALLOW_TO_JOIN;
  }
};

export const useClientSpecificIsAdmin = () => {
  const { user } = useMytaverse();
  const { currentParticipant } = useMytaverseEvent();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return (
        currentParticipant?.eventRole === EventRole.Admin ||
        user?.hrData?.persona?.toUpperCase() ===
          IXRHrPersonaTypes.HR_Facilitator
      );
    }

    default:
      return currentParticipant?.eventRole === EventRole.Admin;
  }
};

export const useClientSpecificCanCreateNewAvatar = (
  listOfAvatarsAlreadyCreated: IUserAvatar[],
): boolean => {
  const useAvaturnHelper = getUseAvaturnHelper();
  const { getProcessedBrand } = useAvaturnHelper();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      const brand = getProcessedBrand();

      // For non SD (HR), we can only create 1 avatar
      if (brand !== IXRHrBrandTypes.SD) {
        const canCreateHRAvatar =
          listOfAvatarsAlreadyCreated.length === 0 ? true : false;
        return canCreateHRAvatar;
      }

      // for SD, we can create up to 5 avatars: 4 non uniformed and 1 uniformed
      const maxNumAvatar = parseInt(env.REACT_APP_MAX_NUMBER_OF_AVATARS || '5');

      if (listOfAvatarsAlreadyCreated.length < maxNumAvatar - 1) {
        return true;
      } else {
        // if there are already 4 in the list BUT one of them is the uniform, then we can add
        const hasUniform = listOfAvatarsAlreadyCreated.some((obj) =>
          obj.tags.includes('UNIFORM'),
        );
        if (hasUniform && listOfAvatarsAlreadyCreated.length < maxNumAvatar) {
          return true;
        }
        return false;
      }
    }
    default:
      return (
        listOfAvatarsAlreadyCreated.length <
        parseInt(env.REACT_APP_MAX_NUMBER_OF_AVATARS || '3')
      );
  }
};

export const useClientSpecificCustomTextDeleteEditActionsOnAvatar = (
  avatar: IUserAvatar | null,
) => {
  const useAvaturnHelper = getUseAvaturnHelper();
  const { getProcessedBrand } = useAvaturnHelper();
  let canEdit = true,
    canDelete = true,
    cannotEditAvatarNotification,
    customDeleteAvatarTitle = '',
    customDeleteAvatarText = '';

  if (avatar) {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK': {
        const brand = getProcessedBrand();
        if (brand === IXRHrBrandTypes.SD && avatar.tags?.includes('UNIFORM')) {
          customDeleteAvatarTitle = 'deleteUniformedAvatarModal.title';
          customDeleteAvatarText = 'deleteUniformedAvatarModal.text';
          canEdit = false;
          cannotEditAvatarNotification = 'cannotEditAvatar';
          canDelete = true;
        }
        break;
      }
    }
  }

  return {
    canEdit,
    canDelete,
    cannotEditAvatarNotification,
    customDeleteAvatarTitle,
    customDeleteAvatarText,
  };
};

const hideGenericAvaturnTypeForEk = (brand: string | undefined) => {
  if (brand) {
    switch (brand.toUpperCase()) {
      case IXRHrBrandTypes.SD:
        return true;
      default:
        return false;
    }
  } else {
    return false;
  }
};

export const useClientSpecificHideGenericAvaturnType = () => {
  const useAvaturnHelper = getUseAvaturnHelper();
  const { getProcessedBrand } = useAvaturnHelper();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return hideGenericAvaturnTypeForEk(getProcessedBrand());
    }
    default:
      return false;
  }
};

export const useClientSpecificHideRevokeAvatarConsentLink = () => {
  const useAvaturnHelper = getUseAvaturnHelper();
  const { getProcessedBrand } = useAvaturnHelper();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      const brand = getProcessedBrand();
      if (brand === IXRHrBrandTypes.SD) {
        return true;
      } else {
        return false;
      }
    }
    default:
      return false;
  }
};

export const useClientSpecificHideAvaturnCreation = () => {
  const { user } = useMytaverse();
  const useAvaturnHelper = getUseAvaturnHelper();
  const { getProcessedBrand } = useAvaturnHelper();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      if (
        getProcessedBrand() === IXRHrBrandTypes.SD &&
        user?.hrData?.persona === IXRHrPersonaTypes.Abinitio
      ) {
        const hasUniformed = user?.avatars.find(({ tags = [] }) =>
          tags.includes('UNIFORM'),
        );

        return !!hasUniformed;
      }
      return false;
    }
    default:
      return false;
  }
};

export const useClientSpecificHideAvatarPolicyModal = () => {
  const useAvaturnHelper = getUseAvaturnHelper();
  const { getProcessedBrand } = useAvaturnHelper();
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return getProcessedBrand() === IXRHrBrandTypes.SD;
    }
    default:
      return false;
  }
};

export const useClientSpecificNoEventsNotification = () => {
  const { user } = useMytaverse();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      if (user.hrData.brand.toUpperCase() === IXRHrBrandTypes.SD) {
        return false;
      }
      return true;
    }
    default:
      return true;
  }
};

export const useClientSpecificAvatarDeletion = () => {
  const { user, sendMessageToUnreal } = useMytaverse();

  const { currentEventParticipant, currentEvent, setCurrentEventParticipant } =
    useMytaverseEvent();

  return useCallback(
    async (avatar: IUserAvatar) => {
      switch (REACT_APP_CLIENT_ENV) {
        case 'EK': {
          if (
            user.hrData.brand.toUpperCase() == IXRHrBrandTypes.SD &&
            currentEventParticipant?.state &&
            currentEvent?.eventId &&
            avatar.tags?.includes('UNIFORM')
          ) {
            const state = currentEventParticipant?.state
              ? JSON.parse(currentEventParticipant.state)
              : null;

            if (state.AssistantHistory) {
              state.AssistantHistory = state.AssistantHistory.replace(
                /\(TagName="iXR\.Maya\.SS[^"]*"\),?/g,
                '',
              )
                .replace(/,\)/g, ')')
                .replace(/,\)/g, ')')
                .replace(/\(\s*\)/g, '');

              state.bIsUniformTryOnCompleted = false;
              state.bIsMagicMirrorCompleted = false;
              state.bIsCrowningMomentCompleted = false;

              await EventsService.setEventUserData(currentEvent.eventId, {
                state: JSON.stringify(state),
              });

              sendMessageToUnreal({
                action: WebsocketAction.EventProfileStateUpdated,
              });

              setCurrentEventParticipant((prevState) => {
                if (!prevState) {
                  return prevState;
                }

                return {
                  ...prevState,
                  state: JSON.stringify(state),
                };
              });
            }

            return;
          }
          return;
        }
        default:
          return;
      }
    },
    [user, currentEventParticipant, currentEvent?.eventId, sendMessageToUnreal],
  );
};

export const useClientSpecificSDScreenShareButtonDisabled = () => {
  const { user } = useMytaverse();
  const { currentEventParticipant } = useMytaverseEvent();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return (
        user.hrData.brand.toUpperCase() === IXRHrBrandTypes.SD &&
        currentEventParticipant?.role === EventRole.None
      );
    }

    default:
      return false;
  }
};

export const useClientSpecificLogo = () => {
  const { user } = useMytaverse();
  // this code is called also when logged out so we cannot use useAvaturnHelper()

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      if (user) {
        if (user.hrData.brand.toUpperCase() !== IXRHrBrandTypes.SD) {
          return {
            logo: APP_LOGO.DEFAULT,
            shouldStickToTheTop: false,
          };
        } else {
          return {
            logo: APP_LOGO.ALT,
            shouldStickToTheTop: true,
          };
        }
      } else {
        const currentURL = window.location.href;
        if (currentURL.includes(ROUTES.CUSTOM_AVATURN)) {
          return {
            logo: APP_LOGO.ALT,
            shouldStickToTheTop: true,
          };
        } else {
          return {
            logo: APP_LOGO.DEFAULT,
            shouldStickToTheTop: false,
          };
        }
      }
    }
    default:
      return {
        logo: APP_LOGO.DEFAULT,
        shouldStickToTheTop: false,
      };
  }
};
